import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot, ChevronDown } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const quickReplies = [
  "Tarifs réparation iPhone",
  "Réparation MacBook",
  "Prendre RDV",
  "Horaires et accès",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Bonjour ! Je suis l'assistant de l'Atelier du Mac. Comment puis-je vous aider ? (réparation iPhone, Mac, iPad, Watch, tarifs, RDV...)",
      suggestions: quickReplies,
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: "user", text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: text,
        history: messages.slice(-6).map((m) => ({ role: m.role, content: m.text })),
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: res.data.response,
          suggestions: res.data.suggestions || [],
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Désolé, une erreur s'est produite. Contactez-nous directement.", suggestions: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-20 right-6 z-50 w-14 h-14 bg-[#FF5C00] hover:bg-[#E05200] text-white rounded-full shadow-lg shadow-[#FF5C00]/30 flex items-center justify-center transition-all duration-200 hover:scale-110"
        aria-label="Ouvrir le chat"
      >
        {open ? <ChevronDown className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat window */}
      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-36 right-6 z-50 w-80 sm:w-96 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden border border-white/10"
          style={{ background: "#121215" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1A1A1E] border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#FF5C00]/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-[#FF5C00]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Assistant Atelier</p>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#34C759]" />
                  <span className="text-xs text-white/50">En ligne</span>
                </div>
              </div>
            </div>
            <button
              data-testid="chat-close-btn"
              onClick={() => setOpen(false)}
              className="p-1 text-white/40 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="h-72 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i}>
                <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-[#FF5C00] text-white rounded-br-sm"
                        : "bg-[#1A1A1E] text-white/90 rounded-bl-sm"
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
                {msg.role === "bot" && msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 ml-0">
                    {msg.suggestions.map((s, si) => (
                      <button
                        key={si}
                        data-testid={`chat-suggestion-${si}`}
                        onClick={() => sendMessage(s)}
                        className="text-xs px-3 py-1.5 rounded-full border border-white/20 text-white/70 hover:border-[#FF5C00] hover:text-[#FF5C00] transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1E] rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-white/30 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/10">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage(input);
              }}
              className="flex gap-2"
            >
              <input
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 bg-[#1A1A1E] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={!input.trim() || loading}
                className="w-10 h-10 bg-[#FF5C00] hover:bg-[#E05200] disabled:opacity-40 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
